import React from 'react'
import PropTypes from 'prop-types'
import detail from './detail.module.css'
import classes from './index.module.css'

/**
 * The Create tab "YouTube Music Trending" list, as the daily chart sweep
 * left it (content-admin/ingest's chartSweepDone -> planTrendingUpdate).
 * `result.trending` is the only field this reads - everything else on a
 * chart-sweep request (`result.chart`, `result.rows`) is the per-artist
 * sync bookkeeping the rest of this page already shows elsewhere.
 *
 * Every field here is optional: a sweep saved before this history was
 * added has only `matched`/`skipped_unmatched`/`skipped_not_visible`/
 * `list_size`, never `chart`/`before`/`after`/`added`/`removed` - this
 * renders only what exists rather than guessing at the rest.
 */

function tally(n, one, many) {
  return `${n} ${n === 1 ? one : many}`
}

const OUTCOME_LABEL = {
  placed: row => (row.position ? `Placed #${row.position}` : 'Placed'),
  not_found: () => 'Not in catalog',
  hidden: () => 'Hidden',
  duplicate: () => 'Duplicate',
  cut: () => 'Cut by the 48-song cap',
}

function outcomeLabel(row) {
  const fn = OUTCOME_LABEL[row.outcome]
  return fn ? fn(row) : row.outcome || '—'
}

// Codes are shown as plain words, never raw (HANDOFF.md §1).
const REMOVED_REASON = {
  cap: 'cut by the 48-song cap',
  not_visible: 'no longer visible',
}

function nameArtist(entry) {
  const name = entry.name || entry.f_id
  return entry.artist ? `${name} — ${entry.artist}` : name
}

// Counts come from `chart` when saved; older sweeps have only the four
// counters, so they fall back to matched minus hidden.
function summaryLine(trending, chart) {
  const hasChart = chart.length > 0
  const placedCount = hasChart
    ? chart.filter(row => row.outcome === 'placed').length
    : Math.max((trending.matched || 0) - (trending.skipped_not_visible || 0), 0)
  const rows = hasChart ? `${placedCount} of ${chart.length}` : `${placedCount}`
  const rowWord = (hasChart ? chart.length : placedCount) === 1 ? 'chart row' : 'chart rows'
  const cutCount = hasChart ? chart.filter(row => row.outcome === 'cut').length : 0
  const cutPart = cutCount > 0 ? `, ${tally(cutCount, 'song cut by the 48-song cap', 'songs cut by the 48-song cap')}` : ''
  return `Placed ${rows} ${rowWord}, ${tally(trending.skipped_unmatched || 0, 'song not in the catalogue', 'songs not in the catalogue')}, ${tally(trending.skipped_not_visible || 0, 'song hidden', 'songs hidden')}${cutPart}, list size ${trending.list_size ?? 0}.`
}

export default function TrendingSummary({ trending }) {
  if (!trending) return null

  if (trending.error) {
    return (
      <section className={detail.section}>
        <h2 className={detail.sectionTitle}>Trending list</h2>
        <p className={detail.notice}>The trending list could not be updated this time: {trending.error}</p>
      </section>
    )
  }

  const chart = Array.isArray(trending.chart) ? trending.chart : []
  const added = Array.isArray(trending.added) ? trending.added : []
  const removed = Array.isArray(trending.removed) ? trending.removed : []
  const after = Array.isArray(trending.after) ? trending.after : []

  return (
    <section className={detail.section}>
      <h2 className={detail.sectionTitle}>Trending list</h2>

      {trending.skipped === 'empty_list' ? (
        <p className={detail.sectionNote}>
          The trending list was not changed — none of today’s chart songs are in the catalogue and visible yet.
        </p>
      ) : trending.skipped === 'empty_clean_list' ? (
        <p className={detail.sectionNote}>
          The trending list was not changed — every song on the new list is explicit, so the clean list would end up empty.
        </p>
      ) : (
        <p className={detail.sectionNote}>{summaryLine(trending, chart)}</p>
      )}

      {added.length > 0 && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>{tally(added.length, 'song added', 'songs added')}</h3>
          <ul className={detail.plainList}>
            {added.map(entry => (
              <li key={entry.f_id}>{nameArtist(entry)}</li>
            ))}
          </ul>
        </div>
      )}

      {removed.length > 0 && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>{tally(removed.length, 'song removed', 'songs removed')}</h3>
          <ul className={detail.plainList}>
            {removed.map(entry => (
              <li key={entry.f_id}>
                {nameArtist(entry)}
                <span className={detail.summaryWhere}> — {REMOVED_REASON[entry.reason] || entry.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {chart.length > 0 && (
        <details className={detail.section}>
          <summary className={detail.disclosureSummary}>Chart rows ({chart.length})</summary>
          <div className={detail.disclosureBody}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.head}>Rank</th>
                  <th className={classes.head}>Title</th>
                  <th className={classes.head}>Artists</th>
                  <th className={classes.head}>Result</th>
                </tr>
              </thead>
              <tbody>
                {chart.map(row => (
                  <tr key={`${row.rank}-${row.video_id}`} className={classes.row}>
                    <td className={classes.cell}>{row.rank}</td>
                    <td className={classes.cell}>{row.title}</td>
                    <td className={classes.cell}>{row.artists}</td>
                    <td className={classes.cell}>{outcomeLabel(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {after.length > 0 && (
        <details className={detail.section}>
          <summary className={detail.disclosureSummary}>New list order ({after.length})</summary>
          <div className={detail.disclosureBody}>
            <ol className={detail.plainList}>
              {after.map(entry => (
                <li key={entry.f_id}>
                  {nameArtist(entry)} <span className={detail.summaryWhere}>({entry.from === 'chart' ? 'chart' : 'kept'})</span>
                </li>
              ))}
            </ol>
          </div>
        </details>
      )}
    </section>
  )
}

TrendingSummary.propTypes = { trending: PropTypes.object }
